import React, { useState, useEffect, useRef } from 'react';
import { communicationsAPI, providerCompaniesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, MessageSquare, Edit, Trash2, Send, Clock, FileText, Users, Image, Video, Paperclip, X, Play, Eye } from 'lucide-react';

const STATUS_CONFIG = {
  DRAFT: { label: 'Borrador', color: 'bg-slate-100 text-slate-800' },
  SCHEDULED: { label: 'Programado', color: 'bg-blue-100 text-blue-800' },
  SENT: { label: 'Enviado', color: 'bg-green-100 text-green-800' },
};

// Media Preview Component
const MediaPreview = ({ url, type, onRemove }) => {
  const isImage = type === 'image' || url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isVideo = type === 'video' || url.match(/\.(mp4|webm|mov)$/i);
  
  return (
    <div className="relative group">
      <div className="w-24 h-24 rounded-lg overflow-hidden border bg-slate-100">
        {isImage && (
          <img src={url} alt="Preview" className="w-full h-full object-cover" />
        )}
        {isVideo && (
          <div className="w-full h-full flex items-center justify-center bg-slate-900">
            <Play className="w-8 h-8 text-white" />
          </div>
        )}
        {!isImage && !isVideo && (
          <div className="w-full h-full flex items-center justify-center">
            <FileText className="w-8 h-8 text-slate-400" />
          </div>
        )}
      </div>
      {onRemove && (
        <button
          onClick={onRemove}
          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default function CommunicationsPage() {
  const [communications, setCommunications] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingComm, setEditingComm] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    target_provider_ids: null,
    scheduled_at: '',
    is_draft: false,
    media_urls: [],
    attachment_urls: []
  });
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewComm, setPreviewComm] = useState(null);
  
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [commsRes, providersRes] = await Promise.all([
        communicationsAPI.getAll(null, true),
        providerCompaniesAPI.getAll()
      ]);
      setCommunications(commsRes.data);
      setProviders(providersRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    setUploading(true);
    try {
      const response = await communicationsAPI.uploadMedia(file);
      const url = response.data.url;
      
      if (type === 'media') {
        setFormData(prev => ({
          ...prev,
          media_urls: [...(prev.media_urls || []), url]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          attachment_urls: [...(prev.attachment_urls || []), url]
        }));
      }
      toast.success('Archivo subido exitosamente');
    } catch (error) {
      toast.error('Error al subir archivo');
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = (index) => {
    setFormData(prev => ({
      ...prev,
      media_urls: prev.media_urls.filter((_, i) => i !== index)
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachment_urls: prev.attachment_urls.filter((_, i) => i !== index)
    }));
  };

  const handleCreate = async (asDraft = false) => {
    try {
      if (!formData.title || !formData.content) {
        toast.error('Título y contenido son requeridos');
        return;
      }
      const data = {
        ...formData,
        target_provider_ids: selectedProviders.length > 0 ? selectedProviders : null,
        is_draft: asDraft
      };
      await communicationsAPI.create(data);
      toast.success(asDraft ? 'Borrador guardado' : 'Comunicado creado');
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear comunicado');
    }
  };

  const handleUpdate = async () => {
    try {
      const data = {
        ...formData,
        target_provider_ids: selectedProviders.length > 0 ? selectedProviders : null
      };
      await communicationsAPI.update(editingComm.id, data);
      toast.success('Comunicado actualizado');
      setEditingComm(null);
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleSend = async (id) => {
    try {
      await communicationsAPI.send(id);
      toast.success('Comunicado enviado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este comunicado?')) return;
    try {
      await communicationsAPI.delete(id);
      toast.success('Comunicado eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Solo se pueden eliminar borradores');
    }
  };

  const resetForm = () => {
    setFormData({ 
      title: '', 
      content: '', 
      target_provider_ids: null, 
      scheduled_at: '', 
      is_draft: false,
      media_urls: [],
      attachment_urls: []
    });
    setSelectedProviders([]);
  };

  const openEditDialog = (comm) => {
    setEditingComm(comm);
    setFormData({
      title: comm.title,
      content: comm.content,
      scheduled_at: comm.scheduled_at || '',
      is_draft: comm.status === 'DRAFT',
      media_urls: comm.media_urls || [],
      attachment_urls: comm.attachment_urls || []
    });
    setSelectedProviders(comm.target_provider_ids || []);
    setShowDialog(true);
  };

  const toggleProviderSelection = (providerId) => {
    setSelectedProviders(prev =>
      prev.includes(providerId)
        ? prev.filter(id => id !== providerId)
        : [...prev, providerId]
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-EC', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="communications-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Comunicados</h1>
          <p className="text-slate-600">Envía comunicados a los proveedores</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) { setEditingComm(null); resetForm(); }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-comm-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Comunicado
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingComm ? 'Editar Comunicado' : 'Crear Nuevo Comunicado'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Título *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Título del comunicado"
                  data-testid="comm-title-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Contenido *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Escribe el contenido del comunicado..."
                  className="w-full min-h-32 p-3 border rounded-lg resize-y"
                  data-testid="comm-content-input"
                />
              </div>
              
              {/* Media Upload Section */}
              <div>
                <label className="text-sm font-medium mb-2 block">Adjuntar Multimedia</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="file"
                    ref={imageInputRef}
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'media')}
                  />
                  <input
                    type="file"
                    ref={videoInputRef}
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'media')}
                  />
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'attachment')}
                  />
                  
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-image-btn"
                  >
                    <Image className="w-4 h-4 mr-2" />
                    Imagen
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-video-btn"
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Video
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-file-btn"
                  >
                    <Paperclip className="w-4 h-4 mr-2" />
                    Archivo
                  </Button>
                </div>
                
                {uploading && (
                  <div className="text-sm text-slate-500 mb-2">Subiendo archivo...</div>
                )}
                
                {/* Media Previews */}
                {formData.media_urls && formData.media_urls.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-slate-500 mb-2">Imágenes/Videos:</p>
                    <div className="flex gap-2 flex-wrap">
                      {formData.media_urls.map((url, idx) => (
                        <MediaPreview
                          key={idx}
                          url={url}
                          onRemove={() => removeMedia(idx)}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Attachment Previews */}
                {formData.attachment_urls && formData.attachment_urls.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Archivos adjuntos:</p>
                    <div className="flex gap-2 flex-wrap">
                      {formData.attachment_urls.map((url, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-700 max-w-32 truncate">
                            {url.split('/').pop()}
                          </span>
                          <button
                            onClick={() => removeAttachment(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Destinatarios
                  <span className="text-slate-400 text-xs ml-2">
                    (Dejar vacío para enviar a todos)
                  </span>
                </label>
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  {providers.map(provider => (
                    <label
                      key={provider.id}
                      className="flex items-center gap-3 p-2 hover:bg-slate-50 cursor-pointer border-b last:border-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedProviders.includes(provider.id)}
                        onChange={() => toggleProviderSelection(provider.id)}
                        className="rounded"
                      />
                      <span className="text-sm">{provider.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Programar envío (opcional)</label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                />
              </div>
              <div className="flex gap-2 pt-4">
                {!editingComm && (
                  <Button variant="outline" onClick={() => handleCreate(true)} className="flex-1">
                    <FileText className="w-4 h-4 mr-2" />
                    Guardar Borrador
                  </Button>
                )}
                <Button
                  onClick={editingComm ? handleUpdate : () => handleCreate(false)}
                  className="flex-1"
                  data-testid="save-comm-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {editingComm ? 'Actualizar' : (formData.scheduled_at ? 'Programar' : 'Enviar Ahora')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Communications List */}
      <div className="space-y-4">
        {communications.map(comm => {
          const statusConfig = STATUS_CONFIG[comm.status];
          const canEdit = comm.status !== 'SENT';
          const hasMedia = comm.media_urls && comm.media_urls.length > 0;
          const hasAttachments = comm.attachment_urls && comm.attachment_urls.length > 0;
          
          return (
            <Card key={comm.id} data-testid={`comm-card-${comm.id}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className={statusConfig?.color}>{statusConfig?.label}</Badge>
                      {comm.target_provider_ids ? (
                        <Badge variant="outline" className="text-xs">
                          <Users className="w-3 h-3 mr-1" />
                          {comm.target_provider_ids.length} proveedores
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          <Users className="w-3 h-3 mr-1" />
                          Todos
                        </Badge>
                      )}
                      {hasMedia && (
                        <Badge variant="outline" className="text-xs">
                          <Image className="w-3 h-3 mr-1" />
                          {comm.media_urls.length} media
                        </Badge>
                      )}
                      {hasAttachments && (
                        <Badge variant="outline" className="text-xs">
                          <Paperclip className="w-3 h-3 mr-1" />
                          {comm.attachment_urls.length} archivos
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-lg">{comm.title}</h3>
                    <p className="text-slate-600 text-sm mt-1 line-clamp-2">{comm.content}</p>
                    
                    {/* Media Preview in Card */}
                    {hasMedia && (
                      <div className="flex gap-2 mt-3">
                        {comm.media_urls.slice(0, 3).map((url, idx) => (
                          <div key={idx} className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100">
                            {url.match(/\.(mp4|webm|mov)$/i) ? (
                              <div className="w-full h-full flex items-center justify-center bg-slate-800">
                                <Play className="w-6 h-6 text-white" />
                              </div>
                            ) : (
                              <img src={url} alt="" className="w-full h-full object-cover" />
                            )}
                          </div>
                        ))}
                        {comm.media_urls.length > 3 && (
                          <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 text-sm font-medium">
                            +{comm.media_urls.length - 3}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                      <span>Por: {comm.created_by_name}</span>
                      {comm.sent_at && <span>Enviado: {formatDate(comm.sent_at)}</span>}
                      {comm.scheduled_at && comm.status === 'SCHEDULED' && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Programado: {formatDate(comm.scheduled_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setPreviewComm(comm)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {canEdit && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(comm)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        {comm.status === 'DRAFT' && (
                          <Button variant="ghost" size="icon" onClick={() => handleSend(comm.id)}>
                            <Send className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        {comm.status === 'DRAFT' && (
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(comm.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {communications.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No hay comunicados</p>
          <p className="text-sm">Crea tu primer comunicado para los proveedores</p>
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewComm} onOpenChange={() => setPreviewComm(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Vista Previa del Comunicado</DialogTitle>
          </DialogHeader>
          {previewComm && (
            <div className="space-y-4">
              <div>
                <Badge className={STATUS_CONFIG[previewComm.status]?.color}>
                  {STATUS_CONFIG[previewComm.status]?.label}
                </Badge>
              </div>
              <h2 className="text-2xl font-bold">{previewComm.title}</h2>
              
              {/* Media Gallery */}
              {previewComm.media_urls && previewComm.media_urls.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {previewComm.media_urls.map((url, idx) => (
                    <div key={idx} className="aspect-video rounded-lg overflow-hidden bg-slate-100">
                      {url.match(/\.(mp4|webm|mov)$/i) ? (
                        <video src={url} controls className="w-full h-full object-cover" />
                      ) : (
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-slate-700 whitespace-pre-wrap">{previewComm.content}</p>
              
              {/* Attachments */}
              {previewComm.attachment_urls && previewComm.attachment_urls.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Archivos adjuntos:</p>
                  <div className="flex gap-2 flex-wrap">
                    {previewComm.attachment_urls.map((url, idx) => (
                      <a
                        key={idx}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg hover:bg-slate-200 transition-colors"
                      >
                        <FileText className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-700">
                          {url.split('/').pop()}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="text-sm text-slate-400 border-t pt-4">
                <p>Por: {previewComm.created_by_name}</p>
                <p>Creado: {formatDate(previewComm.created_at)}</p>
                {previewComm.sent_at && <p>Enviado: {formatDate(previewComm.sent_at)}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
